/**
 * VoiceRecorder — Reusable mic recorder.
 *
 * MediaRecorder API → audio/webm;codecs=opus blob. Emits onStop(blob) when
 * the user stops. Parent component handles upload + response.
 *
 * States: idle | recording | stopping. Shows elapsed timer + stop button
 * while recording, a mic button when idle.
 *
 * Props:
 *   onStart      — () => void (recording actually started)
 *   onStop       — (blob: Blob) => void
 *   onError      — (msg: string) => void
 *   disabled     — boolean (parent is busy)
 *   compact      — smaller footprint for inline use
 */

import { useEffect, useRef, useState } from "react";

export default function VoiceRecorder({ onStart, onStop, onError, disabled, compact = false }) {
  const [state, setState] = useState("idle"); // idle | recording | stopping
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const start = async () => {
    if (state !== "idle" || disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setState("idle");
        setElapsed(0);
        if (blob.size > 0) onStop?.(blob);
      };
      mr.start(1000);
      recorderRef.current = mr;
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000);
      onStart?.();
    } catch (err) {
      const msg =
        err?.name === "NotAllowedError"
          ? "Microphone permission denied"
          : err?.message || "Could not start recording";
      onError?.(msg);
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    setState("stopping");
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      recorderRef.current?.stop();
    } catch {
      // ignore
    }
  };

  const fmtTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (compact) {
    return (
      <button
        type="button"
        onClick={state === "recording" ? stop : start}
        disabled={disabled || state === "stopping"}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${
          state === "recording"
            ? "border-red-300 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
            : "border-neutral-300 dark:border-white/20 bg-white dark:bg-slate-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/5"
        }`}
      >
        {state === "recording" ? (
          <>
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span>Stop · {fmtTime(elapsed)}</span>
          </>
        ) : (
          <>
            <MicIcon className="w-3.5 h-3.5" />
            <span>Record</span>
          </>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={state === "recording" ? stop : start}
      disabled={disabled || state === "stopping"}
      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50 ${
        state === "recording"
          ? "border-red-400 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          : "border-neutral-300 dark:border-white/20 bg-white dark:bg-slate-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/5"
      }`}
    >
      {state === "recording" ? (
        <>
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          <span>Stop recording · {fmtTime(elapsed)}</span>
        </>
      ) : state === "stopping" ? (
        <span>Processing...</span>
      ) : (
        <>
          <MicIcon className="w-4 h-4" />
          <span>Start with voice</span>
        </>
      )}
    </button>
  );
}

function MicIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0m7 7v3m0 0h3m-3 0H9m3-3a3 3 0 01-3-3V6a3 3 0 016 0v8a3 3 0 01-3 3z" />
    </svg>
  );
}
